import { Link } from "react-router-dom";
import { Instagram, Phone, Mail, MapPin, Facebook } from "lucide-react";

const SUBSTACK_URL =
    "https://substack.com/@coralbaycatering?r=1wil5o&utm_medium=ios&utm_source=stories&shareImageVariant=blur";
const FACEBOOK_URL =
    "https://www.facebook.com/share/1FjP6AiPPj/?mibextid=wwXIfr";
const INSTAGRAM_URL = "https://instagram.com/coralbay_catering";

function SubstackIcon({ className = "w-4 h-4" }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden="true"
        >
            <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
        </svg>
    );
}

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="bg-forest-900 text-sand-100 mt-24"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-4 gap-12">
                <div className="md:col-span-2">
                    <img
                        src="/coral-bay-logo-light.png"
                        alt="Coral Bay Catering"
                        data-testid="footer-logo"
                        className="h-20 w-auto mb-6"
                    />
                    <p className="text-sand-100/70 max-w-md leading-relaxed">
                        Fresh. Local. Innovative. Made with love on St. John, USVI for
                        over twenty-three years.
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-social-instagram"
                            aria-label="Instagram"
                            className="w-10 h-10 rounded-full border border-sand-100/30 flex items-center justify-center hover:bg-sand-100 hover:text-forest-900 transition-colors"
                        >
                            <Instagram className="w-4 h-4" strokeWidth={1.5} />
                        </a>
                        <a
                            href={FACEBOOK_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-social-facebook"
                            aria-label="Facebook"
                            className="w-10 h-10 rounded-full border border-sand-100/30 flex items-center justify-center hover:bg-sand-100 hover:text-forest-900 transition-colors"
                        >
                            <Facebook className="w-4 h-4" strokeWidth={1.5} />
                        </a>
                        <a
                            href={SUBSTACK_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-social-substack"
                            aria-label="Substack"
                            className="w-10 h-10 rounded-full border border-sand-100/30 flex items-center justify-center hover:bg-sand-100 hover:text-forest-900 transition-colors"
                        >
                            <SubstackIcon className="w-3.5 h-3.5" />
                        </a>
                    </div>
                </div>

                <div>
                    <div className="overline !text-sand-100/60 mb-4">Explore</div>
                    <ul className="space-y-2 text-sand-100/80">
                        <li>
                            <Link
                                to="/packages"
                                data-testid="footer-link-packages"
                                className="underline-link"
                            >
                                Menu Packages
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/weddings"
                                data-testid="footer-link-weddings"
                                className="underline-link"
                            >
                                Weddings & Banquets
                            </Link>
                        </li>
                        <li>
                            <Link to="/book" data-testid="footer-link-book" className="underline-link">
                                Reserve a Date
                            </Link>
                        </li>
                        <li>
                            <Link to="/about" data-testid="footer-link-about" className="underline-link">
                                About
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/contact"
                                data-testid="footer-link-contact"
                                className="underline-link"
                            >
                                Contact
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/admin/login"
                                data-testid="footer-link-admin"
                                className="underline-link text-sand-100/40"
                            >
                                Admin
                            </Link>
                        </li>
                    </ul>
                </div>

                <div>
                    <div className="overline !text-sand-100/60 mb-4">Contact</div>
                    <ul className="space-y-3 text-sand-100/80">
                        <li className="flex items-start gap-2">
                            <Phone className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                            <a
                                href="tel:3404236611"
                                data-testid="footer-phone"
                                className="underline-link"
                            >
                                340-423-6611
                            </a>
                        </li>
                        <li className="flex items-start gap-2">
                            <Mail className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                            <a
                                href="mailto:coralbaycatering@gmail.com"
                                data-testid="footer-email"
                                className="underline-link break-all"
                            >
                                coralbaycatering@gmail.com
                            </a>
                        </li>
                        <li className="flex items-start gap-2">
                            <Instagram className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-instagram"
                                className="underline-link"
                            >
                                @coralbay_catering
                            </a>
                        </li>
                        <li className="flex items-start gap-2">
                            <Facebook className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                            <a
                                href={FACEBOOK_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-facebook"
                                className="underline-link"
                            >
                                Facebook
                            </a>
                        </li>
                        <li className="flex items-start gap-2">
                            <SubstackIcon className="w-4 h-4 mt-1 shrink-0" />
                            <a
                                href={SUBSTACK_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-substack"
                                className="underline-link"
                            >
                                Substack
                            </a>
                        </li>
                        <li className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                            <span>9901 Emmaus, St. John, USVI 00830</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-sand-100/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between text-xs text-sand-100/50">
                    <span>© {new Date().getFullYear()} Coral Bay Catering</span>
                    <span>Crafted with love on St. John, USVI</span>
                </div>
            </div>
        </footer>
    );
}
